import { getEndpoint } from "./schemas/endpoints.js";
import { ErrorEnvelopeSchema } from "./schemas/primitives.js";
import { HEALTH_AI_CONSENT_REQUIRED_CODE, HealthAiConsentRequestSchema, MePatchRequestSchema } from "./schemas/me.js";
import { HardConstraintsPutRequestSchema, ProfilePatchRequestSchema, } from "./schemas/profile.js";
import { OnboardingStepPutRequestSchema, } from "./schemas/onboarding.js";
import { PlanJobCreateRequestSchema, } from "./schemas/planJobs.js";
import { PlanApplyRequestSchema, PlanProposalCandidatesRequestSchema, PlanProposalRequestSchema, PlanUndoRequestSchema, } from "./schemas/plans.js";
import { PantryConfirmationRequestSchema, PantryExtractionRequestSchema, PantryItemUpdateRequestSchema, } from "./schemas/pantry.js";
import { ShoppingListPatchRequestSchema, ShoppingPurchaseRequestSchema, } from "./schemas/shopping.js";
import { PrepareBatchRequestSchema, } from "./schemas/preparedBatches.js";
import { MealOutcomeRequestSchema, } from "./schemas/meals.js";
import { MemoryCandidateConfirmRequestSchema, MemoryCandidateCreateRequestSchema, } from "./schemas/memory.js";
import { AmyTurnCompleteRequestSchema, AmyTurnRegisterRequestSchema, } from "./schemas/amyTurns.js";
import { ProgressWeightLogRequestSchema, } from "./schemas/progress.js";
import { BillingCheckoutRequestSchema, BillingOffersQuerySchema, BillingAppleTransactionRequestSchema, } from "./schemas/billing.js";
import { OffPlanEntryCreateRequestSchema, } from "./schemas/ledger.js";
import { DeletionJobRequestSchema, } from "./schemas/jobs.js";
import { IngredientResolveRequestSchema, } from "./schemas/ingredients.js";
import { ComposeRecipeRequestSchema, } from "./schemas/composeRecipe.js";
import { LlmUsageRecordRequestSchema, } from "./schemas/usage.js";
/**
 * Minimal typed fetch client factory for the BiteBalance MVP endpoint
 * surface. Plain `fetch` only (no axios) so this works unmodified in the
 * browser (T09 web) and React Native (T19 Expo). Every request/response is
 * validated against the same Zod schemas that drive OpenAPI generation, so
 * a contract drift fails loudly at the client boundary instead of silently
 * shipping bad data.
 */
export class ApiError extends Error {
    status;
    envelope;
    constructor(status, envelope) {
        super(`${envelope.code}: ${envelope.messageKey}`);
        this.name = "ApiError";
        this.status = status;
        this.envelope = envelope;
    }
}
/** True for the 403 envelope every health-data / AI endpoint answers without an active consent. */
export function isHealthAiConsentRequired(err) {
    return err instanceof ApiError && err.status === 403 && err.envelope.code === HEALTH_AI_CONSENT_REQUIRED_CODE;
}
/**
 * F14: the 409 codes that mean "the plan preview you are holding can never be applied again" — a NEWER preview
 * superseded it (TOKEN_SUPERSEDED), a racing apply already consumed it (TOKEN_CONSUMED) or its 30 minutes ran out
 * (TOKEN_EXPIRED). Re-sending the same token can only fail again, so a client treats these exactly like a stale
 * revision: drop the held preview and request a fresh one. (STALE_REVISION keeps its own handling.)
 */
export const DEAD_PLAN_PREVIEW_CODES = ["TOKEN_SUPERSEDED", "TOKEN_CONSUMED", "TOKEN_EXPIRED"];
/** True for a 409 whose code is one of DEAD_PLAN_PREVIEW_CODES. */
export function isDeadPlanPreview(err) {
    return err instanceof ApiError && err.status === 409 && DEAD_PLAN_PREVIEW_CODES.includes(err.envelope.code);
}
function defaultIdempotencyKey() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
export function createClient(config) {
    const fetchImpl = config.fetchImpl ?? fetch;
    const genKey = config.generateIdempotencyKey ?? defaultIdempotencyKey;
    const baseUrl = config.baseUrl.replace(/\/$/, "");
    async function call(operationId, opts = {}) {
        const endpoint = getEndpoint(operationId);
        let pathname = endpoint.path;
        for (const [key, value] of Object.entries(opts.pathParams ?? {})) {
            pathname = pathname.replace(`{${key}}`, encodeURIComponent(value));
        }
        let url = `${baseUrl}${pathname}`;
        if (opts.query) {
            const search = new URLSearchParams();
            for (const [key, value] of Object.entries(opts.query)) {
                if (value !== undefined)
                    search.set(key, value);
            }
            const qs = search.toString();
            if (qs)
                url += `?${qs}`;
        }
        const headers = { "Content-Type": "application/json", ...config.defaultHeaders };
        if (!endpoint.isWebhook) {
            const token = await config.getToken();
            if (token)
                headers.Authorization = `Bearer ${token}`;
        }
        if (endpoint.requiresIdempotencyKey && !endpoint.isWebhook) {
            headers["Idempotency-Key"] = genKey();
        }
        // Validate the outgoing payload against the same schema used for OpenAPI
        // generation before it ever hits the wire.
        const validatedBody = endpoint.body && opts.body !== undefined ? endpoint.body.parse(opts.body) : opts.body;
        const res = await fetchImpl(url, {
            method: endpoint.method.toUpperCase(),
            headers,
            body: validatedBody !== undefined ? JSON.stringify(validatedBody) : undefined,
            cache: "no-store",
        });
        const json = res.status === 204 ? undefined : await res.json().catch(() => undefined);
        if (!res.ok) {
            const parsedError = ErrorEnvelopeSchema.safeParse(json);
            if (res.status === 403 && parsedError.success && parsedError.data.code === HEALTH_AI_CONSENT_REQUIRED_CODE) {
                try {
                    config.onHealthAiConsentRequired?.();
                }
                catch {
                    // A failing listener must never mask the API error itself.
                }
            }
            throw new ApiError(res.status, parsedError.success
                ? parsedError.data
                : {
                    code: "UNKNOWN_ERROR",
                    messageKey: "errors.unknown",
                    correlationId: res.headers.get("x-correlation-id") ?? "unknown",
                    retryable: res.status >= 500,
                });
        }
        return endpoint.response.parse(json);
    }
    return {
        getMe: () => call("getMe"),
        /**
         * Sets the language the API answers CATALOGUE names in (`users.locale`).
         * The client dictionary is a separate thing and this does not touch it —
         * see schemas/me.ts for why the two drifted apart.
         */
        patchMe: (body) => call("patchMe", { body: MePatchRequestSchema.parse(body) }),
        /** Records the caller's explicit health/AI consent at `version` (the current wording). */
        postHealthAiConsent: (body) => call("postHealthAiConsent", { body: HealthAiConsentRequestSchema.parse(body) }),
        /** Withdraws the health/AI consent and erases the health data it covered (Art. 7(3)); one transaction, replay-safe. */
        deleteHealthAiConsent: () => call("deleteHealthAiConsent"),
        /** #100 version handshake: the currently deployed Amy behavior version. */
        getAmyVersion: () => call("getAmyVersion"),
        // Typed as the endpoint's actual response schema (ProfileResponseSchema:
        // Profile + personalInfo + healthConditions) — closes the long-documented
        // client.ts typing gap the frontends had to cast around.
        getProfile: () => call("getProfile"),
        patchProfile: (body) => call("patchProfile", { body: ProfilePatchRequestSchema.parse(body) }),
        createProfilePhotoUpload: () => call("createProfilePhotoUpload", { body: {} }),
        getHardConstraints: () => call("getHardConstraints"),
        putHardConstraints: (body) => call("putHardConstraints", {
            body: HardConstraintsPutRequestSchema.parse(body),
        }),
        getOnboarding: () => call("getOnboarding"),
        putOnboardingStep: (step, body) => call("putOnboardingStep", {
            pathParams: { step: String(step) },
            body: OnboardingStepPutRequestSchema.parse(body),
        }),
        createPlanJob: (body) => call("createPlanJob", { body: PlanJobCreateRequestSchema.parse(body) }),
        getPlanJob: (id) => call("getPlanJob", { pathParams: { id } }),
        getCurrentPlan: () => call("getCurrentPlan"),
        createPlanProposal: (planId, body) => call("createPlanProposal", {
            pathParams: { id: planId },
            body: PlanProposalRequestSchema.parse(body),
        }),
        listPlanProposalCandidates: (planId, body) => call("listPlanProposalCandidates", {
            pathParams: { id: planId },
            body: PlanProposalCandidatesRequestSchema.parse(body),
        }),
        applyPlanProposal: (planId, proposalId, body) => call("applyPlanProposal", {
            pathParams: { id: planId, proposalId },
            body: PlanApplyRequestSchema.parse(body),
        }),
        undoPlan: (planId, body) => call("undoPlan", {
            pathParams: { id: planId },
            body: PlanUndoRequestSchema.parse(body),
        }),
        getRecipe: (id) => call("getRecipe", { pathParams: { id } }),
        resolveIngredient: (body) => call("resolveIngredient", {
            body: IngredientResolveRequestSchema.parse(body),
        }),
        composeRecipe: (body) => call("composeRecipe", {
            body: ComposeRecipeRequestSchema.parse(body),
        }),
        getPantry: () => call("getPantry"),
        extractPantryItems: (body) => call("extractPantryItems", {
            body: PantryExtractionRequestSchema.parse(body),
        }),
        confirmPantryItems: (body) => call("confirmPantryItems", {
            body: PantryConfirmationRequestSchema.parse(body),
        }),
        updatePantryItem: (itemId, body) => call("updatePantryItem", {
            pathParams: { id: itemId },
            body: PantryItemUpdateRequestSchema.parse(body),
        }),
        removePantryItem: (itemId) => call("removePantryItem", { pathParams: { id: itemId } }),
        getOffers: (scope) => call("getOffers", { query: { scope } }),
        getCurrentShoppingList: () => call("getCurrentShoppingList"),
        patchCurrentShoppingList: (body) => call("patchCurrentShoppingList", {
            body: ShoppingListPatchRequestSchema.parse(body),
        }),
        getPreparedBatches: () => call("getPreparedBatches", {}),
        prepareBatch: (body) => call("prepareBatch", { body: PrepareBatchRequestSchema.parse(body) }),
        confirmShoppingPurchases: (body) => call("confirmShoppingPurchases", {
            body: ShoppingPurchaseRequestSchema.parse(body),
        }),
        recordMealOutcome: (mealId, body) => call("recordMealOutcome", {
            pathParams: { id: mealId },
            body: MealOutcomeRequestSchema.parse(body),
        }),
        createMemoryCandidate: (body) => call("createMemoryCandidate", {
            body: MemoryCandidateCreateRequestSchema.parse(body),
        }),
        getMemoryCandidates: () => call("getMemoryCandidates"),
        confirmMemoryCandidate: (id, body) => call("confirmMemoryCandidate", {
            pathParams: { id },
            body: MemoryCandidateConfirmRequestSchema.parse(body),
        }),
        rejectMemoryCandidate: (id) => call("rejectMemoryCandidate", { pathParams: { id }, body: {} }),
        getLearnedPreferences: () => call("getLearnedPreferences"),
        deleteLearnedPreference: (id) => call("deleteLearnedPreference", { pathParams: { id } }),
        getInsightsValue: () => call("getInsightsValue"),
        logProgressWeight: (body) => call("logProgressWeight", {
            body: ProgressWeightLogRequestSchema.parse(body),
        }),
        getProgressWeightHistory: () => call("getProgressWeightHistory"),
        getProgressSummary: () => call("getProgressSummary"),
        getDossier: () => call("getDossier"),
        getAmyHistory: () => call("getAmyHistory"),
        getAmyConversation: () => call("getAmyConversation"),
        startNewAmyConversation: () => call("startNewAmyConversation"),
        // ADR-079: the runtime registers/completes a turn; the clients read its status.
        registerAmyTurn: (body) => call("registerAmyTurn", { body: AmyTurnRegisterRequestSchema.parse(body) }),
        getAmyTurn: (id) => call("getAmyTurn", { pathParams: { id } }),
        completeAmyTurn: (id, body) => call("completeAmyTurn", { pathParams: { id }, body: AmyTurnCompleteRequestSchema.parse(body) }),
        getLlmUsage: () => call("getLlmUsage"),
        recordLlmUsage: (body) => call("recordLlmUsage", { body: LlmUsageRecordRequestSchema.parse(body) }),
        /**
         * `query.platform: "ios"` asks for the iOS-aware response (ADR-085: Apple
         * `purchaseChannel` when the server flag is on). No argument = today's
         * contract, byte for byte.
         */
        getBillingOffers: (query) => call("getBillingOffers", query ? { query: BillingOffersQuerySchema.parse(query) } : undefined),
        getProductState: () => call("getProductState"),
        getDayLedger: (date) => call("getDayLedger", date ? { query: { date } } : undefined),
        createOffPlanEntry: (body) => call("createOffPlanEntry", {
            body: OffPlanEntryCreateRequestSchema.parse(body),
        }),
        deleteOffPlanEntry: (id) => call("deleteOffPlanEntry", { pathParams: { id } }),
        createBillingCheckout: (body) => call("createBillingCheckout", {
            body: BillingCheckoutRequestSchema.parse(body),
        }),
        getBillingSubscription: () => call("getBillingSubscription"),
        /**
         * ADR-085: submit a signed StoreKit 2 transaction (purchase or restore).
         * Every call is a NEW request with a fresh Idempotency-Key (generated in
         * `call`); never cache or replay a previous answer.
         */
        submitAppleTransaction: (body) => call("submitAppleTransaction", {
            body: BillingAppleTransactionRequestSchema.parse(body),
        }),
        /** ADR-085: the 14-day free period, granted server-side once per account. */
        grantFreeAccess: () => call("grantFreeAccess", { body: {} }),
        cancelBillingSubscription: () => call("cancelBillingSubscription", { body: {} }),
        createExportJob: () => call("createExportJob", { body: {} }),
        createDeletionJob: (body) => call("createDeletionJob", {
            body: DeletionJobRequestSchema.parse(body),
        }),
    };
}
//# sourceMappingURL=client.js.map